import { useEffect, useMemo, useRef } from "react";
import { useAtomValue } from "jotai";
import { segmentationVolumeIdAtom } from "@/atoms/segmentationAtoms";
import { getCornerstone, getCornerstoneTools } from "@/utils/dicomLoader";
import { loadMaskAsContours } from "@/utils/niftiMaskLoader";
import { setSegmentColor } from "@/utils/segmentationConstants";
import { getContourSegmentationId } from "@/utils/viewportToolSetup";
import { logger } from "@/utils/logger";
import type { JointReviewStudy, Mark, Reader } from "@/types/jointReview";

type Rgba = [number, number, number, number];

/** Reader A and Reader B outline colours (blue and yellow stay apart for
 * red-green colour vision); the legend names them, so the colour is never the
 * only cue. Agreed marks draw in their reader's colour, faint and dashed. */
export const READER_COLOR: Record<Reader, Rgba> = {
  A: [56, 189, 248, 255],
  B: [250, 204, 21, 255],
};

/** A blind review's markers: one neutral colour, the same for every item. */
export const MARKER_COLOR: Rgba = [244, 244, 245, 255];

export const markColor = (reader: Reader | null): Rgba =>
  reader === null ? MARKER_COLOR : READER_COLOR[reader];

const MASK_POLL_MS = 300;

/** The viewer-local segment index of every mark: one per mark across both
 * readers' masks, so two readers' stored labels never share a colour slot. */
export function localSegmentIndices(marks: readonly Mark[]): Map<Mark, number> {
  const sorted = [...marks].sort(
    (a, b) => a.mask_id.localeCompare(b.mask_id) || a.segment_index - b.segment_index,
  );
  return new Map(sorted.map((mark, index) => [mark, index + 1]));
}

function viewportsShowing(seriesUid: string): any[] {
  const shown: any[] = [];
  for (const engine of getCornerstone().getRenderingEngines?.() ?? []) {
    for (const viewport of (engine.getViewports?.() ?? []) as any[]) {
      const ids: string[] = viewport.getImageIds?.() ?? [];
      if (ids[0]?.includes(seriesUid) && viewport.getImageData?.()?.imageData) shown.push(viewport);
    }
  }
  return shown;
}

/** A mark's contour style: the current item bold, agreed marks faint and
 * dashed, and every outline off while the reader has hidden the marks. A blind
 * review's markers all have an item, so they never draw as agreed. */
export function markStyle(mark: Mark, currentItem: string | null, visible: boolean) {
  const agreed = mark.item_id === null;
  const width = mark.item_id !== null && mark.item_id === currentItem ? 2.5 : 1.25;
  const opacity = visible ? (agreed ? 0.45 : 1) : 0;
  return {
    renderOutline: visible,
    renderOutlineInactive: visible,
    outlineWidth: width,
    outlineWidthInactive: width,
    outlineOpacity: opacity,
    outlineOpacityInactive: opacity,
    outlineDash: agreed ? "4,4" : "",
    outlineDashInactive: agreed ? "4,4" : "",
    renderFill: false,
    renderFillInactive: false,
  };
}

/** Colour and line style of every loaded mark on every viewport. */
function styleMarks(
  segmentationVolumeId: string,
  locals: ReadonlyMap<Mark, number>,
  currentItem: string | null,
  visible: boolean,
) {
  const csTools = getCornerstoneTools();
  const contourSegId = getContourSegmentationId(segmentationVolumeId);
  const contourType = csTools.Enums?.SegmentationRepresentations?.Contour;
  const style = csTools.segmentation?.config?.style;
  for (const [mark, index] of locals) {
    if (contourType && style?.setStyle)
      style.setStyle(
        {
          type: contourType,
          segmentationId: contourSegId,
          segmentIndex: index,
        },
        markStyle(mark, currentItem, visible),
      );
    for (const engine of getCornerstone().getRenderingEngines?.() ?? [])
      for (const viewport of (engine.getViewports?.() ?? []) as any[]) {
        try {
          setSegmentColor(
            csTools.segmentation.config,
            viewport.id,
            contourSegId,
            index,
            markColor(mark.reader),
          );
        } catch {
          // A viewport without the contour representation yet is styled on the next pass.
        }
        viewport.render?.();
      }
  }
}

/**
 * Draw the study's masks (both readers' round-1 masks, or a blind review's
 * markers), read-only, on whichever panes show their series. Each mask loads
 * once, the first time a pane shows its series; jumping to an item on another
 * series loads that one. Contours
 * from `loadMaskAsContours` are locked, so nothing here can be edited or saved.
 * `visible` false hides every outline (a mask that loads meanwhile too) without
 * unloading anything, so showing them again is immediate.
 */
export function useJointReviewMasks(
  study: JointReviewStudy | undefined,
  currentItem: string | null,
  visible = true,
) {
  const segmentationVolumeId = useAtomValue(segmentationVolumeIdAtom);
  // Masks and marks are fixed for a study; presigned URLs change on every poll,
  // so the load keys on the mask's id and reads the URL when it loads.
  const marks = study?.marks;
  const masks = study?.masks;
  const locals = useMemo(() => localSegmentIndices(marks ?? []), [marks]);
  const loaded = useRef(new Set<string>());
  const loading = useRef(new Set<string>());
  const latest = useRef({ masks, locals, currentItem, visible });
  latest.current = { masks, locals, currentItem, visible };

  useEffect(() => {
    if (!segmentationVolumeId || !masks) return;
    const segId = segmentationVolumeId;
    const timer = window.setInterval(() => {
      const { masks: current, locals: indices } = latest.current;
      for (const mask of current ?? []) {
        const key = mask.mask_id;
        if (loaded.current.has(key) || loading.current.has(key)) continue;
        const viewport = viewportsShowing(mask.series_instance_uid)[0];
        if (!viewport?.element) continue;
        // A mark whose voxels a later mark replaced has no label in the file.
        const own = [...indices].filter(([mark]) => mark.in_mask && mark.mask_id === mask.mask_id);
        loading.current.add(key);
        loadMaskAsContours(mask.mask_url, segId, viewport, viewport.element, {
          localIndicesByStoredSegment: new Map(
            own.map(([mark, index]) => [mark.segment_index, index]),
          ),
          includedStoredSegmentIndices: new Set(own.map(([mark]) => mark.segment_index)),
        })
          .then(() => {
            loaded.current.add(key);
            const { locals: now, currentItem: item, visible: shown } = latest.current;
            styleMarks(segId, now, item, shown);
          })
          .catch((error: unknown) =>
            logger.error(`[JointReview] mask ${mask.mask_id} failed to load:`, error),
          )
          .finally(() => loading.current.delete(key));
      }
    }, MASK_POLL_MS);
    return () => window.clearInterval(timer);
  }, [segmentationVolumeId, masks]);

  useEffect(() => {
    if (segmentationVolumeId && loaded.current.size > 0)
      styleMarks(segmentationVolumeId, locals, currentItem, visible);
  }, [segmentationVolumeId, locals, currentItem, visible]);
}
